import type { PictureInfo, PictureVariant, ProviderKind, SyncOperation } from '@skysa/core';
import Dexie, { type Table, type Transaction } from 'dexie';

import type { EditorMode } from './editorMode.js';
import { letGoWhenFrozen } from './frozenTab.js';
import { watchForHeldUp } from './heldUp.js';
import { isLiveSource } from './rowStates.js';
import { watchForNewerTab } from './staleTab.js';

/**
 * The local store. The app boots and renders from here before any network call,
 * and every read and write works offline; sync is a separate concern layered on
 * top. See docs/ARCHITECTURE.md §7.
 */

/**
 * IndexedDB cannot index a boolean, and the sync engine needs to find dirty
 * notes by index rather than by scanning every row. 0/1 it is.
 */
export type Flag = 0 | 1;

/**
 * Until a storage account is connected, every row belongs to this stand-in
 * connection. The column exists from the start so attaching a real connection
 * is a data migration (`store/connection.ts`) rather than a schema change.
 */
export const LOCAL_CONNECTION_ID = 'local';

export interface NoteRecord {
	/** Local UUID. Stable across renames and moves, and written into frontmatter. */
	id: string;
	connectionId: string;
	/** POSIX path relative to the app root, including the `.md` extension. */
	path: string;
	title: string;
	/** Markdown with frontmatter removed. The source of truth for the note. */
	body: string;
	/** Raw YAML frontmatter, or null if the file has none yet. */
	frontmatter: string | null;
	tags: string[];
	/** Provider file id, or path on WebDAV. Absent until the note has been pushed. */
	remoteId?: string;
	/** Opaque provider version: etag, rev, cTag, headRevisionId. Compare only. */
	remoteVersion?: string;
	/**
	 * SHA-256 of the file as this note and its remote file last agreed on it —
	 * pulled or pushed — unlike `contentHash`, which follows every edit. Only the
	 * sync store writes it; see `SyncNote.syncedHash` in `@skysa/core`. Not
	 * indexed, so rows from before it need no migration: absent reads as "cannot
	 * say".
	 */
	syncedHash?: string;
	/** SHA-256 of the serialized file as last written or last seen remotely. */
	contentHash: string;
	/**
	 * The file, byte for byte, as it stands for this note right now: what a pull
	 * brought in, or what the last edit here serialized to. This — not a fresh
	 * `noteFileContents` — is what the sync engine is handed, because the two
	 * differ for any file the app did not write: one with no frontmatter gains a
	 * block, a `updated: 2026-01-01T00:00:00Z` gains milliseconds. An engine
	 * handed the re-serialized version sees a change nobody made, and a store
	 * that stored the re-serialized version rewrites every note it pulls.
	 *
	 * Every writer that changes what the file says keeps this in step —
	 * `applyEdit`, `createNote`, `importNoteFile`, and the sync store. Deleting
	 * and restoring are not edits to the file: they pin it as it was before they
	 * move `updatedAt`, and a restored note pushes what it held.
	 * Absent on
	 * rows written before it existed, which fall back to `noteFileContents`:
	 * nothing had pulled those, so the app wrote every byte of them.
	 */
	source?: string;
	/** Set only by a real user edit, never by load, mode switch or re-serialize. */
	dirty: Flag;
	/**
	 * Where the body came from: a fresh random token every time the body is
	 * written in from outside this device's editors — a pull, an import, the
	 * remote side of a conflict — and left alone by every edit made here.
	 * Absent reads as `''`.
	 *
	 * An editor holds edits for a moment before they are saved, and `dirty` says
	 * nothing about those, so a pull can replace a clean note's body, or delete
	 * the note, underneath them. Each edit carries the origin of the body it was
	 * typed into, and `saveNoteBody` does not write one made against another
	 * body over it. A token rather than a count, so a row deleted and written
	 * again can never come back with an origin an editor already holds.
	 */
	bodyOrigin?: string;
	/** Tombstone: kept until the delete has been pushed, so sync can replay it. */
	deletedLocally: Flag;
	createdAt: number;
	updatedAt: number;
	/**
	 * Which editor this note was last open in. Local only — it says nothing
	 * about the file, so it is never written to frontmatter and never syncs.
	 * Changing it must not mark the note dirty.
	 */
	editorMode?: EditorMode;
}

export interface FolderRecord {
	connectionId: string;
	/** POSIX path relative to the app root. The root itself is not stored. */
	path: string;
	remoteId?: string;
	createdAt: number;
}

export interface SyncStateRecord {
	connectionId: string;
	provider?: ProviderKind;
	/**
	 * The provider's id for the account this connection is to, once the API has
	 * named it. Kept per connection rather than per device since Phase 7: with
	 * several sources connected at once there is no single "the account", and
	 * this is what says whose files a source's notes are — which a reconnect
	 * asks of a detached source, to know whether the rows it kept are going home
	 * (`bindingMode` in `store/connection.ts`). Absent on rows written before
	 * it, and by an API too old to name accounts.
	 */
	accountId?: string;
	/**
	 * What the server calls the account — an email address, for the most part —
	 * as it last said it (`rememberAccount` in `store/connection.ts`). Kept here
	 * so that a source can still be named once the server will no longer answer
	 * for it: a credential revoked from another device leaves nobody to ask, and
	 * "Dropbox · ann@example.com" is how the user knows which notes are in
	 * question. A label and nothing else; `accountId` is what says whose files
	 * these are. Not indexed, so it needs no version of its own, and absent on
	 * rows written before it.
	 */
	displayName?: string;
	/**
	 * What the *user* calls this source, where they have said. Their word beats
	 * every derived name (`tabName` in `sync/account.ts`), and clearing it puts
	 * the derived one back rather than leaving the tab blank.
	 *
	 * Kept apart from `displayName` because the two answer to different people:
	 * `displayName` is the server's last word on whose account this is and is
	 * overwritten whenever the server is asked, which would throw a rename away
	 * on the next reconcile. This is only ever written from the tab bar.
	 *
	 * This device only, deliberately. The credential, the cursor and the pile
	 * are already per device (docs/ARCHITECTURE.md §6), and a name is the least of them
	 * — a laptop calling a connection "Work" does not make it "Work" on a
	 * phone. Not indexed, so it needs no version of its own, and absent on
	 * every row until someone renames one.
	 */
	label?: string;
	/**
	 * The clipboard is shown for this source, here (docs/ARCHITECTURE.md §7,
	 * "The clipboard"). This device only, as `label` is: one device showing it
	 * says nothing about another, and the items stay in the folder whichever
	 * shows them. Not indexed, so it needs no version of its own; dropped with
	 * the binding (`detachedFrom`), so a source connected again starts without.
	 */
	clipboard?: true;
	/** Opaque provider cursor; persisted only after a batch commits. */
	cursor?: string;
	rootId?: string;
	lastSyncAt?: number;
	/** Random per browser install, reported in the marker file for debugging. */
	clientId: string;
	/**
	 * The provider access token `apps/api` last minted, so a reload does not
	 * need a round trip (docs/ARCHITECTURE.md §8). Short-lived; never a refresh token,
	 * which never leaves the server. Never in localStorage.
	 */
	accessToken?: string;
	/** Epoch milliseconds. */
	accessTokenExpiresAt?: number;
	/**
	 * Resumed with rows that still name remote files, and not yet checked
	 * against the remote (`verifyResume` in `store/connection.ts`). The sync
	 * store writes nothing for the connection until it is.
	 */
	resumeUnverified?: true;
	/**
	 * When this connection was last bound (`bindConnection`), so that what a tab
	 * remembers about the connection as it was — a note deleted here, whose row
	 * a save must not bring back (`store/deletedHere.ts`) — can be told from what
	 * it remembers about an earlier binding of the same id. A reconnect pulls
	 * the remote afresh, and a note the remote still has is not one the user
	 * deleted before. Dropped with the binding (`detachedFrom`).
	 */
	boundAt?: number;
	/**
	 * The device no longer reaches this source, and it is still here because it
	 * holds something its remote was never sent (`detachConnection` in
	 * `store/connection.ts`). Everything the remote has is gone from the device;
	 * what is left is kept under the source it was written in, where the user
	 * can see it, until they reconnect the account, download it or discard it.
	 * It is never moved to the device's own pile, where it would be invisible
	 * behind any other source, and never into another account.
	 *
	 * A detached source syncs nothing: the scheduler starts no session for it,
	 * the sync store refuses it, and it has no credential, cursor, root or
	 * token. It can still be shown and written in.
	 *
	 * `disconnected`: the user let it go, here. `revoked`: the server stopped
	 * answering for the connection — disconnected from another device, or the
	 * credential revoked. `interrupted`: the row had already gone when a save
	 * from another tab arrived for one of its notes, and was made again to hold
	 * it (`ensureDetached`). Not indexed, so it needs no version of its own.
	 */
	detached?: Detached;
	/**
	 * The files in this source that are not UTF-8 text, which the engine leaves
	 * alone and the storage panel lists (docs/ARCHITECTURE.md §7). Here, beside the
	 * cursor, because they are written with it: one row, one `put`, so the list
	 * never names a file from a batch that did not land. Keyed by `remoteId`
	 * by whoever writes it (`sync/store.ts`). A notice and nothing else —
	 * nothing reads it to decide anything. Not indexed, so it needs no version
	 * of its own, and absent means none.
	 */
	unreadable?: { remoteId: string; path: string; movedAside?: string[] }[];
	/**
	 * Bound for the first time and not yet through its first sync: the import
	 * the user watches and can cancel (`components/account/ImportProgress.tsx`). Set by
	 * `bindConnection` for a source it has not seen, cleared by `finishImport`
	 * once a pull has reached the end and a push has gone through the queue,
	 * and the whole source thrown away by `abandonImport` on a cancel.
	 */
	importing?: Importing;
}

interface Importing {
	/**
	 * The app is held while the import runs, and nothing can be written in the
	 * source meanwhile. True for every import since 2026-10-02. It was true
	 * only for the first source, the one the device's own pile is copied into,
	 * so a row from before that may say `false`: that source could have been
	 * written in, which a cancel still asks about (`writtenSince`). Held either
	 * way now (`useHeldImport`). The pile is kept as it was until the import
	 * finishes, which is what a cancel goes back to.
	 */
	lock: boolean;
	/** The source that was in front before, which a cancel puts back. */
	returnTo: string;
}

export interface Detached {
	/** Epoch milliseconds. */
	at: number;
	reason: 'disconnected' | 'revoked' | 'interrupted';
}

/**
 * A credential this device holds, keyed by the connection it reaches — or by
 * `PENDING_CREDENTIAL_ID` while a flow is out and there is no connection yet.
 *
 * The plaintext lives here and nowhere else on this device. See
 * `store/credentials.ts` for what that buys and what it costs.
 */
export interface CredentialRecord {
	/** A connection id, or `PENDING_CREDENTIAL_ID`. */
	id: string;
	/** `sk1_…`. Never logged, never rendered, never put in a URL. */
	credential: string;
	/** Which provider the flow was for, so a pending row can be shown for what it is. */
	provider: ProviderKind;
	createdAt: number;
}

/** The one flow that may be out at a time, matching the server's one flow cookie. */
export const PENDING_CREDENTIAL_ID = 'pending';

/**
 * App-wide settings. A table rather than `localStorage` because the store is
 * already here, it is the same place everything else lives, and it works the
 * same in a test as in the browser.
 */
interface PreferenceRecord {
	key: string;
	value: string;
}

/** The kinds of op the queue holds: core's, so a new one is a row the app can store. */
type QueuedOperation = SyncOperation;

/**
 * A file beside a note that is not one (#187): one row for every such file the
 * remote has, and for every file added here and not yet uploaded (no
 * `remoteId`). Its bytes, where the device holds them, are in `fileBytes`.
 * `SyncFile` in `packages/core/src/sync/store.ts` says what each kind of row
 * may and may not have done to it.
 */
export interface FileRecord {
	connectionId: string;
	id: string;
	path: string;
	remoteId?: string;
	remoteVersion?: string;
	/** In bytes. */
	size: number;
}

/**
 * A file's bytes, where this device holds them: held until they are uploaded
 * (`pinned`), which nothing may evict, or cached under the remote version they
 * were read at, which the cache's budget may.
 *
 * An `ArrayBuffer` and not a `Blob`. Every browser keeps a large value of
 * either kind out of line, but a `Blob` does not survive the structured clone
 * the tests' IndexedDB makes of it — it comes back an empty object — and bytes
 * that cannot be tested are bytes that cannot be trusted.
 */
export interface FileBytesRecord {
	connectionId: string;
	id: string;
	bytes: ArrayBuffer;
	/** The remote version these bytes are, for cached ones. */
	version?: string;
	/** 1 while the bytes exist nowhere else. A number: IndexedDB indexes no boolean. */
	pinned: 0 | 1;
	/** For evicting the least recently used of the cached ones. */
	lastUsedAt: number;
}

/**
 * An item on a source's clipboard (docs/ARCHITECTURE.md §7, "The clipboard"),
 * keyed by connection and by its name in `CLIPBOARD_FOLDER`, which says when it
 * was pasted and what it is (`readClipName`). Its bytes, where this device
 * holds them, are in `clipBytes`, so listing the clipboard never reads 25 MB.
 *
 * `pending`: pasted here and not uploaded yet, its bytes the only copy.
 * `sent`: the remote has it. `removing`: the user removed it and the remote
 * has not been told yet; shown nowhere.
 */
export interface ClipRecord {
	connectionId: string;
	name: string;
	state: 'pending' | 'sent' | 'removing';
	remoteId?: string;
	version?: string;
	/** In bytes. */
	size: number;
	/** The start of a text, for showing it without reading its bytes. */
	preview?: string;
}

/** A clipboard item's bytes, an `ArrayBuffer` for the reason `FileBytesRecord` gives. */
interface ClipBytesRecord {
	connectionId: string;
	name: string;
	bytes: ArrayBuffer;
}

/**
 * How the clipboard's panel draws a picture on it (#276): from a thumb made on
 * this device from its bytes, or as it is — small enough already, or one the
 * browser could not copy. Keyed as its item is, whose name is stamped with its
 * bytes' hash, so it never stands for other bytes; it goes with the item.
 */
export interface ClipThumbRecord {
	connectionId: string;
	name: string;
	/** Absent where the picture is drawn as it is. */
	thumb?: Readonly<{ bytes: ArrayBuffer; type: string }>;
}

/** A smaller copy of a picture, as `PictureRecord` keeps what is known of it. */
export interface PictureCopyRecord {
	/** What it was written as: WebP, or PNG or JPEG where the browser writes no WebP. */
	type: string;
	width: number;
	height: number;
	/** In bytes. */
	size: number;
	/** For evicting the least recently used. */
	lastUsedAt: number;
}

/**
 * What this device knows of a picture beside a note (#276, `store/pictures.ts`):
 * what its header says, and the smaller copies made of it. Never in the
 * user's folder; made again from the original wherever they are not here.
 *
 * Stamped with the remote id and version of the original they were read
 * from, both absent while it exists only here: once its row is bound to other
 * bytes, all of it is another picture's.
 */
export interface PictureRecord {
	connectionId: string;
	fileId: string;
	remoteId?: string;
	version?: string;
	/** What its header says (`imageInfo`); `null` where it says nothing the app reads. */
	info: PictureInfo | null;
	/**
	 * The browser could not draw a copy of it: it is shown as it is, and no
	 * copy is asked for again while the record stands for these bytes.
	 */
	refused?: true;
	copies: Partial<Record<PictureVariant, PictureCopyRecord>>;
}

/** A copy's bytes, apart from its record, as a file's are from its row. */
interface PictureBytesRecord {
	connectionId: string;
	fileId: string;
	variant: PictureVariant;
	bytes: ArrayBuffer;
}

/**
 * What this device last read of a source's key file for secrets,
 * `.encryption.json` (docs/ARCHITECTURE.md §7, "Secrets in a note"): read when
 * the source's session starts and again when a pull meets the file
 * (`sync/keyFile.ts`). Absent until it has been read once.
 *
 * `none`: the folder has no key file, so the source is not set up. `ok`: it
 * names the recipient every new secret is encrypted to. `newer`: a newer build
 * wrote it, and this one neither encrypts to it nor writes over it. `invalid`:
 * something is there that this app did not write, and setup leaves it alone.
 */
export interface KeyFileRecord {
	connectionId: string;
	state: 'none' | 'ok' | 'newer' | 'invalid';
	/** Where it is `ok`: `age1…`, in lower case. */
	recipient?: string;
	/** Where there is a file: what a replacement writes over. */
	remoteId?: string;
	version?: string;
}

/** How a device unlocks a source's secrets: entered, or the device's own lock. */
export type UnlockKind = 'pattern' | 'pin' | 'password' | 'device';

/**
 * A source's keys for secrets on this device (docs/ARCHITECTURE.md §7, "Secrets
 * in a note"): its identities, the source's own first, sealed under how the
 * device unlocks (`store/secretKeys.ts`). Never sent anywhere, and gone when
 * the source's notes leave the device.
 */
export interface SecretKeyRecord {
	connectionId: string;
	/**
	 * The recipient the device enrolled with, `age1…`: what a secret inserted
	 * here is encrypted to, which takes no unlocking. Not secret.
	 */
	recipient: string;
	unlock: UnlockKind;
	/**
	 * PBKDF2's salt and rounds, which make the sealing key from the unlock; or,
	 * for the device's own lock, the salt its passkey is asked, and no rounds.
	 */
	salt: ArrayBuffer;
	rounds: number;
	/** The passkey the device's own lock answers through, where that is how it unlocks. */
	credentialId?: ArrayBuffer;
	/** AES-GCM's nonce, new for each sealing, and the identities it sealed. */
	iv: ArrayBuffer;
	sealed: ArrayBuffer;
	/** Tries since the last right one, each counted before it is checked. */
	tries: number;
	/** When the last try was made, which a try after the fifth waits on. */
	triedAt?: number;
	/**
	 * The recipients the source's key had before it was replaced, whose keys
	 * the device still holds: a secret made with one of them is encrypted
	 * afresh to `recipient`. Absent where the key was never replaced here.
	 */
	former?: string[];
}

export interface OpQueueRecord {
	seq?: number;
	connectionId: string;
	op: QueuedOperation;
	noteId?: string;
	/** For a file's ops, the file. */
	fileId?: string;
	path: string;
	/** For `move`, where the entry is going. */
	targetPath?: string;
	/**
	 * For `rmdir`, the folder's `remoteId` when it was queued. The row it came
	 * from is gone by then — that is what the op is for — and without the id the
	 * engine will not touch the path. For `delete-file`, the same.
	 */
	remoteId?: string;
	/** For an `upload` whose bytes this device does not hold, a file that has them. */
	copyOf?: string;
	attempts: number;
	lastError?: string;
	queuedAt: number;
}

export type NotesDatabase = Dexie & {
	notes: Table<NoteRecord, NoteKey>;
	folders: Table<FolderRecord, FolderKey>;
	syncState: Table<SyncStateRecord, string>;
	opQueue: Table<OpQueueRecord, number>;
	prefs: Table<PreferenceRecord, string>;
	credentials: Table<CredentialRecord, string>;
	files: Table<FileRecord, FileKey>;
	fileBytes: Table<FileBytesRecord, FileKey>;
	clips: Table<ClipRecord, ClipKey>;
	clipBytes: Table<ClipBytesRecord, ClipKey>;
	clipThumbs: Table<ClipThumbRecord, ClipKey>;
	pictures: Table<PictureRecord, FileKey>;
	pictureBytes: Table<PictureBytesRecord, [string, string, PictureVariant]>;
	keyFiles: Table<KeyFileRecord, string>;
	secretKeys: Table<SecretKeyRecord, string>;
};

const DATABASE_NAME = 'skysa-notes';

/** Where the notes wait while their store is made again under a new key. */
const NOTES_REKEYING = 'notesRekeying';

/** A note's primary key: the source it belongs to, then its id within it. */
export type NoteKey = [connectionId: string, id: string];

/** A file's primary key, in `files` and `fileBytes`, and a picture's of it: its source, then its id. */
export type FileKey = [connectionId: string, id: string];

/** A notebook's primary key: its source, then its path. */
export type FolderKey = [connectionId: string, path: string];

/** A clipboard item's primary key: its source, then its name. */
export type ClipKey = [connectionId: string, name: string];

export const noteKey = (note: Pick<NoteRecord, 'connectionId' | 'id'>): NoteKey => [
	note.connectionId,
	note.id,
];

/**
 * The same, as a string: for a `Map`, a `Set`, a React `key`. An id on its own
 * names a note only inside its source, so state held by bare id is state two
 * sources' notes can share by accident.
 */
export const noteRef = (note: Pick<NoteRecord, 'connectionId' | 'id'>): string =>
	JSON.stringify(noteKey(note));

/** Whether a `noteRef` names a note of this connection. */
export const refIn = (ref: string, connectionId: string): boolean =>
	ref.startsWith(`${JSON.stringify([connectionId]).slice(0, -1)},`);

/**
 * Every source's cursor dropped and `rootId` kept, as "Re-scan" does (`resync`
 * in `sync/scheduler.ts`), so the next sync of each is a scan: what a change to
 * what `isHidden` lets through needs, since a feed names a file again only when
 * it changes. A scan reads nothing this device already holds at its version.
 */
const dropCursors = async (tx: Transaction): Promise<void> => {
	const rows = (await tx.table('syncState').toArray()) as SyncStateRecord[];
	await tx
		.table('syncState')
		.bulkPut(
			rows
				.filter((row) => row.cursor !== undefined)
				.map(({ cursor: _cursor, ...kept }) => kept)
		);
};

/**
 * Built without subclassing Dexie: the table properties are declared through the
 * cast instead, which keeps this module free of `this` and classes.
 */
export const createDatabase = (name: string = DATABASE_NAME): NotesDatabase => {
	const db = new Dexie(name) as NotesDatabase;

	// `[connectionId+path]` is deliberately not declared `&` unique.
	//
	// It could not be, whatever else were true: a tombstone keeps its path until
	// its delete has been pushed, so a note created at a name a deleted one still
	// holds is two rows at one key, by design.
	//
	// A pull batch reaches that state by passing through states that are not it:
	// a note moving out of the way is a change of its own and can come later in
	// the same batch, so the note taking its path lands first and the two
	// briefly share one. A unique index rejects that write, which fails the
	// batch — and since the cursor is persisted only with the batch, the same
	// one is retried for ever and the user's sync never recovers on its own.
	// The reasoning is set out in full on `PullChange` in
	// `packages/core/src/sync/store.ts`.
	//
	// So no live note is knowingly put where another one is, and that is kept by
	// the writers rather than by IndexedDB: `freeName`/`freePath` in
	// `store/naming.ts`, and `takenNamesIn` in `store/notes.ts`.
	//
	// Knowingly is the whole of the claim. Three writers do not look:
	// `restoreNote` lifts a tombstone with no idea whether its path has been
	// taken since, `importNoteFile` writes a file carrying an `id` it has never
	// seen straight to its path whatever is already there, and `moveFolder`
	// rebases a tombstone onto the path it lands on rather than aiming its
	// queued delete somewhere else. Each is answered where it is used: the undo
	// that calls `restoreNote` moves the restored note aside (`undeleteNote`),
	// the engine has `displace-note` for a file arriving on a taken path, and
	// `noteAtPath` in `store/notes.ts` reads the live row first.
	db.version(1).stores({
		notes: 'id, connectionId, path, [connectionId+path], dirty, deletedLocally, updatedAt, remoteId',
		folders: '[connectionId+path], connectionId, path',
		syncState: 'connectionId',
		opQueue: '++seq, connectionId, noteId, path',
	});

	// `editorMode` on a note needs no version of its own: it is not indexed, and
	// IndexedDB stores whatever properties a record happens to carry.
	db.version(2).stores({
		prefs: 'key',
	});

	// Phase 7: the device proves its right to a connection with a credential it
	// generated, in place of the session cookie that used to do it. Keyed by
	// connection id, so holding several is a matter of holding several rows.
	db.version(3).stores({
		credentials: 'id',
	});

	// A note is keyed by its connection *and* its id. Keyed by id alone, two
	// connected sources could not both hold a note with one id — and they do,
	// whenever a folder has been copied from one account to another, since the
	// id travels in the file. The second source then either failed to sync at
	// all or was handed a made-up id for a file that names its own, and wrote it
	// back over the real one. Each source is its own silo (docs/ARCHITECTURE.md §6);
	// the key now says so, as `folders` always has.
	//
	// IndexedDB cannot change a store's key, and Dexie refuses to try, so the
	// rows go through a second store: out in one version, back in the next.
	// Both run in the one `versionchange` transaction an open gets, so a failure
	// anywhere leaves the database at version 3 with every row where it was. A
	// store a version removes is still readable in that version's upgrade.
	db.version(4)
		.stores({ notes: null, [NOTES_REKEYING]: '[connectionId+id]' })
		.upgrade(async (tx) => {
			const rows = (await tx.table('notes').toArray()) as NoteRecord[];
			// Every row has had a `connectionId` since version 1. One without, or
			// with one that is no string, would have no key here, and a failed add fails the upgrade on every open —
			// so it is given the device's own rather than trusted to be there.
			await tx.table(NOTES_REKEYING).bulkAdd(
				rows.map((row) => ({
					...row,
					connectionId:
						typeof (row.connectionId as unknown) === 'string'
							? row.connectionId
							: LOCAL_CONNECTION_ID,
				}))
			);
		});
	db.version(5)
		.stores({
			[NOTES_REKEYING]: null,
			notes: '[connectionId+id], id, connectionId, path, [connectionId+path], dirty, deletedLocally, updatedAt, remoteId',
		})
		.upgrade(async (tx) => {
			await tx.table('notes').bulkAdd(await tx.table(NOTES_REKEYING).toArray());
		});

	// Files beside notes (#187). Keyed by connection and id, as notes are, and
	// found by path and by remote id within a connection. The bytes apart from
	// the rows, so that listing a folder's files never reads 25 MB, and indexed
	// by what eviction asks: the cached ones, least recently used first.
	db.version(6).stores({
		files: '[connectionId+id], connectionId, [connectionId+path], [connectionId+remoteId]',
		fileBytes: '[connectionId+id], connectionId, [pinned+lastUsedAt]',
		opQueue: '++seq, connectionId, noteId, path, fileId',
	});

	// The clipboard a source's devices share (docs/ARCHITECTURE.md §7). Apart
	// from `files` and `fileBytes`: those are the library's, and what counts
	// unsent work, exports, cleans up and evicts there would take these in too.
	db.version(7).stores({
		clips: '[connectionId+name], connectionId',
		clipBytes: '[connectionId+name], connectionId',
	});

	// Smaller copies of pictures (#276), made on this device and kept apart from
	// `files` and `fileBytes` as the clipboard is: they are nobody's work, never
	// sent or exported, and have a budget of their own. The bytes apart from the
	// records, so that eviction and a use written back never rewrite them.
	db.version(8).stores({
		pictures: '[connectionId+fileId], connectionId',
		pictureBytes: '[connectionId+fileId+variant], [connectionId+fileId], connectionId',
	});

	// The thumbs the clipboard's panel draws its pictures from (#276), apart
	// from the items' bytes so that drawing ten never reads 250 MB.
	db.version(9).stores({
		clipThumbs: '[connectionId+name], connectionId',
	});

	// Every source reads its remote once more (`dropCursors`). A build from
	// before the scratchpad (#269) took `.scratchpad/` for hidden, so a cursor
	// it moved on passed over every scratch note another device had sent by
	// then: those notes never came, on a device that updated after they were
	// made.
	db.version(10).stores({}).upgrade(dropCursors);

	// Secrets in a note (docs/ARCHITECTURE.md §7): the last read of each source's
	// key file, and the keys this device holds for it. One row each per source,
	// apart from `syncState`, which every sync run writes and the scheduler
	// watches.
	db.version(11).stores({
		keyFiles: 'connectionId',
		secretKeys: 'connectionId',
	});

	// Every source reads its remote once more, as at version 10, for the vault's
	// passwords (docs/ARCHITECTURE.md §7, "Passwords in the vault"): a build from
	// before them took `.vault/` for hidden, and its cursor passed over every
	// item another device had sent by then.
	db.version(12).stores({}).upgrade(dropCursors);

	return db;
};

/**
 * What the tab watches `db` for, set up before it opens. Only the app's own
 * database is watched: the frozen-tab watch adds a listener to `document`, and
 * a database made for a test, or beside the app's for a moment, would leave one
 * behind it for every one made. Returns the way to stop watching.
 */
export const watchTab = (db: Dexie): (() => void) => {
	// A build with a later version than the last one in `createDatabase`,
	// opening this database in another tab, must find this tab stopped rather
	// than still writing — `store/staleTab.ts` says why Dexie's default is not
	// that.
	watchForNewerTab(db);
	// Whether another tab is holding this one up, for the sync's status to say
	// (`store/heldUp.ts`).
	watchForHeldUp(db);
	// And a frozen tab must not hold reads open that every other tab then waits
	// behind — `store/frozenTab.ts`.
	return letGoWhenFrozen(db);
};

/**
 * Which connected source the app is showing and writing to, or
 * `LOCAL_CONNECTION_ID` while there is none.
 *
 * An explicit, persisted choice since Phase 7. A device may hold several
 * connected sources at once, each its own silo — its own notes, its own queue,
 * its own credential — and the app shows one at a time (docs/ARCHITECTURE.md §6). Until
 * Phase 7 this was `syncState.first()`, which is a coin toss once there are two
 * rows, and would have shown a different source depending on IndexedDB's
 * ordering.
 *
 * The fallbacks matter as much as the preference. A device bound before this
 * existed has a `syncState` row and no preference, and must not be told it has
 * nothing connected; and a preference naming a source that has since been
 * disconnected must not strand the app on a connection with no rows.
 *
 * Every reader and writer in `store/` that is not told a connection asks this,
 * and the writers ask inside their own transaction. Asked outside, a note
 * created while an account is being connected could be written under the
 * connection its rows have just been moved off, where nothing shows or syncs it.
 */
export const ACTIVE_CONNECTION_KEY = 'sync.activeConnection';

export const activeConnectionId = async (
	db: Pick<NotesDatabase, 'syncState' | 'prefs'>
): Promise<string> => {
	// Two keyed reads on the ordinary path, and the whole table only where there
	// is no usable preference. It is called by every reader and writer in
	// `store/`, and a `syncState.toArray()` here would put every live query over
	// notes into that table's observability set — so each sync run, which writes
	// a cursor to it, would re-run every query on the screen.
	const chosen = (await db.prefs.get(ACTIVE_CONNECTION_KEY))?.value;
	// The device's own pile can be chosen by name: it has no row to check, and
	// it is chosen exactly so that a source made again behind it cannot take the
	// screen from it (`ensureDetached`).
	if (chosen === LOCAL_CONNECTION_ID) return chosen;
	if (chosen !== undefined && (await db.syncState.get(chosen)) !== undefined) return chosen;
	// No choice recorded, or one that names a source this device no longer has.
	// One row is not a choice at all; several with no valid preference is a
	// device mid-migration, and the first is as good an answer as any until
	// something records one.
	//
	// The first *live* one, though. A detached source is kept only for what it
	// never sent (`SyncStateRecord.detached`), and winning this over a source
	// that syncs would put the app on a handful of stranded notes while the
	// user's whole working set sat behind it. It is still ahead of the device's
	// own pile: with nothing else connected it is what there is to show, and
	// answering `LOCAL` would hide the very notes it was kept to keep in sight.
	const states = await db.syncState.toArray();
	const first = states.find(isLiveSource) ?? states[0];
	return first?.connectionId ?? LOCAL_CONNECTION_ID;
};

/**
 * Which source a store call is about. Every reader and writer that takes one
 * acts on the source showing (`activeConnectionId`) where it does not say.
 */
export interface ConnectionScope {
	connectionId?: string;
}

export const db = createDatabase();
watchTab(db);
